import { useEffect, useState } from "react";
import { Crown, ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { Avatar } from "@/components/Bits";
import { signed } from "@/lib/share";

const fmtDay = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });

function NightRow({ r }) {
  return (
    <li className={`flex items-center gap-3 rounded-lg px-2 py-1.5 text-sm ${r.me ? "bg-[#B7F52A]/10" : ""}`} data-testid={`night-row-${r.user_id}`}>
      <span className="w-5 shrink-0 text-center font-mono-num text-slate-500">{r.rank}</span>
      <Avatar user={r} size={24} />
      <span className="min-w-0 flex-1">
        <span className="block truncate font-semibold">{r.display_name}{r.me && <span className="ml-1 text-[11px] font-normal text-slate-400">(toi)</span>}</span>
        <span className="block text-[11px] text-slate-400">{r.correct}/{r.picks} bons pronos · <span className="text-[#B7F52A]">{signed(r.won)}</span> / <span className="text-rose-300">{signed(r.lost)}</span></span>
      </span>
      <span className="font-mono-num font-bold">{signed(r.points)}</span>
    </li>
  );
}

export default function BestNightCard({ leagueId }) {
  const [day, setDay] = useState(null);
  const [d, setD] = useState(null);
  const [all, setAll] = useState(false);
  useEffect(() => { setDay(null); }, [leagueId]);
  useEffect(() => { setAll(false); }, [day]);
  useEffect(() => {
    api.get(`/leagues/${leagueId}/best-night`, { params: day ? { day } : {} }).then((r) => setD(r.data)).catch(() => setD(null));
  }, [leagueId, day]);
  if (!d?.day || !d.rows.length) return null;
  const shown = all ? d.rows : d.rows.slice(0, 3);
  return (
    <section className="mpl-card mpl-feature relative overflow-hidden p-5" data-testid="best-night-card">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="mpl-eyebrow flex items-center gap-1.5"><Crown size={13} /> Meilleur de la soirée</p>
          <p className="mt-0.5 text-xs text-slate-400 first-letter:uppercase" data-testid="best-night-date">{fmtDay(d.day)}</p>
        </div>
        <div className="mr-6 flex shrink-0 gap-1">
          <button onClick={() => setDay(d.prev)} disabled={!d.prev} aria-label="Soirée précédente" className="mpl-btn-ghost px-2 py-1 disabled:opacity-30" data-testid="best-night-prev"><ChevronLeft size={15} /></button>
          <button onClick={() => setDay(d.next)} disabled={!d.next} aria-label="Soirée suivante" className="mpl-btn-ghost px-2 py-1 disabled:opacity-30" data-testid="best-night-next"><ChevronRight size={15} /></button>
        </div>
      </div>
      {d.best.length ? (
        <div className="mt-3 space-y-2" data-testid="best-night-winners">
          {d.best.map((b) => (
            <div key={b.user_id} className="flex items-center gap-3">
              <Avatar user={b} size={44} />
              <p className="min-w-0 flex-1 truncate font-display text-2xl font-extrabold uppercase" data-testid={`best-night-name-${b.user_id}`}>{b.display_name}</p>
              <p className="font-mono-num text-2xl font-bold text-[#B7F52A]" data-testid={`best-night-points-${b.user_id}`}>{signed(b.points)}</p>
            </div>
          ))}
          {d.best.length > 1 && <p className="text-xs text-slate-400">Égalité en tête.</p>}
        </div>
      ) : <p className="mt-3 text-sm text-slate-300" data-testid="best-night-none">Soirée difficile : personne n'a marqué de points.</p>}
      <ol className="mt-4 space-y-0.5 border-t border-white/[0.06] pt-3" data-testid="best-night-rows">{shown.map((r) => <NightRow key={r.user_id} r={r} />)}</ol>
      {d.rows.length > 3 && (
        <button onClick={() => setAll(!all)} aria-expanded={all} className="mt-2 flex items-center gap-1 text-xs font-semibold text-[#B7F52A]" data-testid="best-night-toggle-all">
          {all ? "Réduire" : `Voir les ${d.rows.length} joueurs`} <ChevronDown size={13} className={all ? "rotate-180" : ""} />
        </button>
      )}
      <p className="mt-2 text-[11px] text-slate-500">{d.players} joueur{d.players > 1 ? "s" : ""} avec un prono ce soir-là.</p>
    </section>
  );
}
